import React from "react";
import styled from "styled-components";
import InfoCircle from "@untitled-ui/icons-react/build/esm/InfoCircle";
import TooltipSimple from "../../../common/tooltip/TooltipSimple";

// #region CONFIG

/**
 * Card type configurations defining the title, table columns, legend, and tooltip
 * for each chart card variant. Data rows are passed via props from the parent.
 */
export const CHART_CARD_TYPES = Object.freeze({
    COMMERCIAL_OUTCOME: {
        key: "commercialOutcome",
        title: "COMMERCIAL OUTCOME — BASE SCENARIO — EST.",
        tableColumns: ["Metric", "{comparableSeason} Actual", "Buy Plan", "Committed order"],
        legendItems: [
            { label: "{comparableSeason} actual", color: "var(--text-and-icons-gray-200, #7c868b)" },
            { label: "Buy plan", color: "var(--bostagea-blue-outline, #0060cc)" },
            { label: "Committed order", color: "var(--text-and-icons-green-500, #00bb00)" }
        ],
        tooltip: {
            header: "Commercial outcome",
            title: "Planning estimates, not accounting forecasts. Base case uses 60% sell-through and 46% gross margin for the Buy Plan, and 55% sell-through and 45% gross margin for the Committed Order."
        }
    },
    CATEGORY_ALLOCATION: {
        key: "categoryAllocation",
        title: "CATEGORY ALLOCATION - BUY PLAN vs COMMITTED ORDER",
        tableColumns: ["Category", "Variance vs Buy Plan"],
        legendItems: [],
        tooltip: {
            header: "Category allocation",
            title: "Shows how the Committed Order differs from the Buy Plan by category. Bars right of zero are above plan; bars left of zero are below plan. Colors indicate the commercial risk level, not whether the variance is positive or negative."
        }
    }
});

// #endregion

// #region SCOPED CSS
const StyledCard = styled.div`
    &.chart-card {
        background: var(--backgrounds-and-lines-n-400, #1e2631);
        border-radius: 4px;
        padding: 0 0 16px 0;
        display: flex;
        flex-direction: column;
        gap: 4px;
        flex: 1;
        min-width: 0;
    }

    /* --- Header: title + divider --- */
    .chart-card__header {
        padding: 16px;
        display: flex;
        flex-direction: column;
        gap: 16px;
        align-self: stretch;
    }

    .chart-card__title-row {
        display: flex;
        flex-direction: row;
        gap: 4px;
        align-items: center;
    }

    .chart-card__title {
        color: var(--text-and-icons-gray-100, #d1d1d1);
        text-align: left;
        font-size: var(--subtitle-2-font-size, 12px);
        font-weight: var(--subtitle-2-font-weight, 400);
    }

    .chart-card__info-icon {
        flex-shrink: 0;
        width: 10px;
        height: 10px;
        overflow: visible;
    }

    .chart-card__divider {
        border: none;
        height: 1px;
        background: #3c485a;
        width: 100%;
        margin: 0;
    }

    /* --- Body: chart area (left) + table (right) --- */
    .chart-card__body {
        display: flex;
        flex-direction: row;
        gap: 4px;
        align-self: stretch;
    }

    .chart-card__chart-area {
        padding: 0 16px;
        display: flex;
        flex-direction: column;
        gap: 10px;
        align-items: flex-start;
        justify-content: center;
        flex: 1;
        min-width: 0;
        overflow: hidden;
    }

    /* Gray placeholder for chart — fills available space */
    .chart-card__chart-placeholder {
        width: 100%;
        min-height: 185px;
        background: #3c485a;
        border-radius: 4px;
    }

    /* --- Table section --- */
    .chart-card__table-section {
        padding: 16px 0;
        display: flex;
        flex-direction: column;
        gap: 8px;
        flex: 1;
        min-width: 0;
        overflow: hidden;
    }

    .chart-card__table-wrapper {
        padding: 0 16px;
        align-self: stretch;
    }

    .chart-card__table {
        background: var(--backgrounds-and-lines-n-600, #10141b);
        border-radius: 8px;
        overflow: hidden;
        width: 100%;
    }

    /* --- Table header row --- */
    .chart-card__thead {
        padding: 9px 8px 6px;
        display: flex;
        flex-direction: column;
        gap: 4px;
    }

    .chart-card__tr {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
        padding: 0 8px;
    }

    .chart-card__th {
        color: var(--text-and-icons-gray-100, #d1d1d1);
        font-size: 12px;
        font-weight: 400;
        flex: 1;
        text-align: center;
    }

    .chart-card__th:first-child {
        text-align: left;
    }

    .chart-card__thead-divider {
        border: none;
        height: 1px;
        background: var(--backgrounds-and-lines-n-100, #3c485a);
        width: 100%;
        margin: 0;
    }

    /* --- Table body rows --- */
    .chart-card__tbody {
        padding: 8px 0 0;
        display: flex;
        flex-direction: column;
        gap: 0;
    }

    .chart-card__data-row {
        padding: 6px 8px;
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
    }

    .chart-card__td {
        flex: 1;
        text-align: center;
        font-size: 12px;
        font-weight: 400;
    }

    .chart-card__td:first-child {
        text-align: left;
        color: var(--text-and-icons-gray-100, #d1d1d1);
    }

    .chart-card__td-value {
        font-size: 12px;
        font-weight: 400;
    }

    .chart-card__td-unit {
        font-size: 10px;
        font-weight: 400;
    }

    /* Cell color modifiers */
    .chart-card__td--actual {
        color: var(--text-and-icons-gray-200, #7c868b);
    }

    .chart-card__td--plan {
        color: var(--text-and-icons-blue-500, #00aeef);
    }

    .chart-card__td--committed {
        color: var(--text-and-icons-green-500, #00bb00);
    }

    .chart-card__td--danger {
        color: var(--text-and-icons-red-500, #ff4d4f);
    }

    .chart-card__td--warning {
        color: var(--text-and-icons-orange-500, #ff9900);
    }

    /* --- Legend footer --- */
    .chart-card__legend {
        padding: 0 16px;
        display: flex;
        flex-direction: row;
        gap: 10px;
        align-items: center;
    }

    .chart-card__legend-item {
        display: flex;
        flex-direction: row;
        gap: 4px;
        align-items: center;
    }

    .chart-card__legend-dot {
        border-radius: 600px;
        flex-shrink: 0;
        width: 6px;
        height: 6px;
    }

    .chart-card__legend-label {
        color: var(--text-and-icons-gray-100, #d1d1d1);
        font-size: 12px;
        font-weight: var(--subtitle-3-font-weight, 400);
    }
`;
// #endregion

/**
 * Color keys for table cell values.
 * Use these as the `colorKey` in each cell of a table row.
 */
export const CELL_COLORS = Object.freeze({
    ACTUAL: "chart-card__td--actual",
    PLAN: "chart-card__td--plan",
    COMMITTED: "chart-card__td--committed",
    DANGER: "chart-card__td--danger",
    WARNING: "chart-card__td--warning"
});

/**
 * BICardWithChart renders a chart card with a chart placeholder on the left
 * and a data table on the right, plus a legend footer.
 *
 * @param {Object} props
 * @param {Object} props.cardType - One of the CHART_CARD_TYPES entries.
 * @param {Array} props.tableRows - Array of row objects, each with:
 *   - label: string — the metric/category name (first column)
 *   - cells: Array of { value: string, unit?: string, colorKey?: CELL_COLORS.* }
 * @param {React.ReactNode} props.children - Optional chart content to render instead of the gray placeholder.
 * @param {string} props.comparableSeasonName - Season name used in dynamic labels.
 */
export default function BICardWithChart({
    cardType = CHART_CARD_TYPES.COMMERCIAL_OUTCOME,
    tableRows = [],
    children,
    comparableSeasonName = "Comparable season"
}) {
    const resolveCopy = value => {
        if (typeof value !== "string") return value;
        return value.replaceAll("{comparableSeason}", comparableSeasonName);
    };

    return (
        <StyledCard className="chart-card">
            {/* Header: title + divider */}
            <div className="chart-card__header">
                <div className="chart-card__title-row">
                    <span className="chart-card__title">{cardType.title}</span>
                    {cardType.tooltip ? (
                        <TooltipSimple
                            header={resolveCopy(cardType.tooltip.header)}
                            title={resolveCopy(cardType.tooltip.title)}
                            placement="top"
                            centered={true}
                        >
                            <InfoCircle className="chart-card__info-icon" style={{ cursor: "pointer" }} />
                        </TooltipSimple>
                    ) : (
                        <InfoCircle className="chart-card__info-icon" />
                    )}
                </div>
                <hr className="chart-card__divider" />
            </div>

            {/* Body: chart + table side by side */}
            <div className="chart-card__body">
                {/* Chart area */}
                <div className="chart-card__chart-area">
                    {children || <div className="chart-card__chart-placeholder" />}
                </div>

                {/* Data table */}
                <div className="chart-card__table-section">
                    <div className="chart-card__table-wrapper">
                        <div className="chart-card__table">
                            {/* Table header */}
                            <div className="chart-card__thead">
                                <div className="chart-card__tr">
                                    {cardType.tableColumns.map(col => (
                                        <div className="chart-card__th" key={col}>
                                            {resolveCopy(col)}
                                        </div>
                                    ))}
                                </div>
                                <hr className="chart-card__thead-divider" />
                            </div>

                            {/* Table body */}
                            <div className="chart-card__tbody">
                                {tableRows.map(row => (
                                    <div className="chart-card__data-row" key={row.key || row.label}>
                                        <div className="chart-card__td">{row.label}</div>
                                        {row.cells.map((cell, cellIdx) => (
                                            <div
                                                className={`chart-card__td ${cell.colorKey || ""}`}
                                                key={`${row.key || row.label}-${cellIdx}`}>
                                                <span className="chart-card__td-value">{cell.value}</span>
                                                {cell.unit && (
                                                    <span className="chart-card__td-unit">{cell.unit}</span>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Legend footer */}
            {cardType.legendItems?.length > 0 && (
                <div className="chart-card__legend">
                    {cardType.legendItems.map(item => (
                        <div className="chart-card__legend-item" key={item.label}>
                            <div
                                className="chart-card__legend-dot"
                                style={{ background: item.color }}
                            />
                            <span className="chart-card__legend-label">{resolveCopy(item.label)}</span>
                        </div>
                    ))}
                </div>
            )}
        </StyledCard>
    );
}
